import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  Chip,
  Typography,
  Alert,
  Grid,
  Card,
  CardContent,
  Avatar,
  CircularProgress,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  VpnKey as VpnKeyIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  LockOpen as LockOpenIcon,
  Lock as LockIcon,
} from '@mui/icons-material';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import userService from '../services/userService';
import { UserDTO, UserRole } from '../types/user.types';

const userValidationSchema = Yup.object().shape({
  clientId: Yup.string().required('Client ID is required'),
  username: Yup.string().required('Username is required').min(3, 'Username must be at least 3 characters'),
  fname: Yup.string().required('First name is required'),
  lname: Yup.string().required('Last name is required'),
  contactNumber: Yup.string().required('Contact number is required').matches(/^[0-9\-+\s()]*$/, 'Invalid phone number'),
  emailId: Yup.string().required('Email is required').email('Invalid email address'),
  bio: Yup.string(),
  facilityId: Yup.string().required('Facility ID is required'),
});

const resetPasswordSchema = Yup.object().shape({
  newPassword: Yup.string()
    .required('Password is required')
    .min(8, 'Password must be at least 8 characters')
    .matches(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*(),.?":{}|<>])/, 'Password must contain uppercase, lowercase, numbers, and special characters'),
  confirmPassword: Yup.string()
    .required('Confirm password is required')
    .oneOf([Yup.ref('newPassword')], 'Passwords must match'),
});

const UserManagementPage: React.FC = () => {
  const [users, setUsers] = useState<UserDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [openResetDialog, setOpenResetDialog] = useState(false);
  const [editingUser, setEditingUser] = useState<UserDTO | null>(null);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [unlockTargetUser, setUnlockTargetUser] = useState<UserDTO | null>(null);
  const [isUnlocking, setIsUnlocking] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [alert, setAlert] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [securityState, setSecurityState] = useState<Record<string, { failedAttempts: number; locked: boolean }>>({});

  const loadSecurityState = () => {
    const loginSecurityKey = 'login_security_state';
    const securityStateStr = localStorage.getItem(loginSecurityKey) || '{}';
    try {
      setSecurityState(JSON.parse(securityStateStr));
    } catch (e) {
      setSecurityState({});
    }
  };

  useEffect(() => {
    loadSecurityState();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchUsers(searchTerm);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const fetchUsers = async (search: string = '') => {
    try {
      setLoading(true);
      const response = await userService.getAllUsers(0, 100, search);
      setUsers(response.content || []);
    } catch (error: any) {
      console.error('Error fetching users:', error);
      setAlert({ type: 'error', message: error.message || 'Failed to load users' });
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmUnlock = async () => {
    if (!unlockTargetUser?.id) return;
    setIsUnlocking(true);
    try {
      await userService.unlockUser(unlockTargetUser.id);
      const loginSecurityKey = 'login_security_state';
      try {
        const currentSecurityState = JSON.parse(localStorage.getItem(loginSecurityKey) || '{}');
        const key = unlockTargetUser.username || unlockTargetUser.email;
        if (key && currentSecurityState[key]) {
          currentSecurityState[key].locked = false;
          currentSecurityState[key].failedAttempts = 0;
          localStorage.setItem(loginSecurityKey, JSON.stringify(currentSecurityState));
          loadSecurityState();
        }
      } catch (e) { }

      setUsers(prev => prev.map(u => u.id === unlockTargetUser.id ? { ...u, accountLocked: false } : u));
      setAlert({ type: 'success', message: `User account unlocked successfully for ${unlockTargetUser.firstName} ${unlockTargetUser.lastName}.` });
      setUnlockTargetUser(null);
    } catch (error: any) {
      console.error('Failed to unlock user account:', error);
      const msg = error?.response?.data?.message || error?.message || 'Failed to unlock user account.';
      setAlert({ type: 'error', message: msg });
    } finally {
      setIsUnlocking(false);
    }
  };

  const filteredUsers = users;

  // Handle add/edit user
  const handleOpenDialog = (user?: UserDTO) => {
    setEditingUser(user || null);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingUser(null);
  };

  const handleSaveUser = async (values: any) => {
    try {

      const phoneValue =
        values.contactNumber && values.contactNumber !== "NA" && values.contactNumber !== "N/A"
          ? values.contactNumber
          : null;

      if (editingUser?.id) {
        // Update existing user
        const updatedUser = await userService.updateUser(editingUser.id, {
          firstName: values.fname,
          lastName: values.lname,
          email: values.emailId,
          phone: phoneValue,
        });
        const updatedUsers = users.map((u) =>
          u.id === editingUser.id ? updatedUser : u
        );
        setUsers(updatedUsers);
        setAlert({ type: 'success', message: 'User updated successfully' });
      } else {
        // Add new user
        const newUser = await userService.createUser({
          username: values.username,
          firstName: values.fname,
          lastName: values.lname,
          email: values.emailId,
          phone: phoneValue,
          role: UserRole.NURSE, // Default role
        });
        setUsers([...users, newUser]);
        setAlert({ type: 'success', message: 'User created successfully' });
      }
      handleCloseDialog();
    } catch (error: any) {
      setAlert({ type: 'error', message: error.message || 'Failed to save user' });
    }
  };

  // Handle delete user
  const handleDeleteUser = async (userId: string) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        await userService.deleteUser(userId);
        setUsers(users.filter((u) => u.id !== userId));
        setAlert({ type: 'success', message: 'User deleted successfully' });
      } catch (error: any) {
        setAlert({ type: 'error', message: error.message || 'Failed to delete user' });
      }
    }
  };

  // Handle reset password
  const handleOpenResetDialog = (userId: string) => {
    setSelectedUserId(userId);
    setOpenResetDialog(true);
  };

  const handleCloseResetDialog = () => {
    setOpenResetDialog(false);
    setSelectedUserId(null);
  };

  const handleResetPassword = async (values: { newPassword: string; confirmPassword: string }) => {
    try {
      if (selectedUserId) {
        setAlert({ type: 'success', message: 'Password reset successfully' });
        handleCloseResetDialog();
      }
    } catch (error) {
      setAlert({ type: 'error', message: 'Failed to reset password' });
    }
  };

  const activeUsers = users.filter((u) => u.isActive !== false).length;
  const inactiveUsers = users.filter((u) => u.isActive === false).length;

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, gap: 2, mb: 3 }}>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 'bold',
            background: 'linear-gradient(135deg, #0066cc 0%, #00478f 100%)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          User Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          sx={{
            bgcolor: "#48CAE4",
            color: "#000000",
            fontWeight: "normal",
            textTransform: 'none',
            "&:hover": { bgcolor: "#3ab3cc" }
          }}
        >
          Add User
        </Button>
      </Box>

      {/* Alert */}
      {alert && (
        <Alert
          severity={alert.type}
          onClose={() => setAlert(null)}
          sx={{ mb: 2 }}
        >
          {alert.message}
        </Alert>
      )}

      {/* Loading */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              background: 'linear-gradient(135deg, #0066cc 0%, #00478f 100%)',
              color: 'white',
            }}
          >
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                {users.length}
              </Typography>
              <Typography variant="body2">Total Users</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)', color: 'white' }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                {activeUsers}
              </Typography>
              <Typography variant="body2">Active Users</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)', color: 'white' }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                {inactiveUsers}
              </Typography>
              <Typography variant="body2">Inactive Users</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Search Bar */}
      <TextField
        fullWidth
        placeholder="Search by username, email, or name"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        variant="outlined"
        size="small"
        sx={{ mb: 3 }}
      />

      {/* Users Table */}
      {!loading && (
        <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
          <Table sx={{ minWidth: 800 }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: 'rgba(0, 102, 204, 0.1)' }}>
                <TableCell sx={{ fontWeight: 'bold', color: '#00478f' }}>User</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: '#00478f' }}>Username</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: '#00478f' }}>Email</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: '#00478f' }}>Contact</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: '#00478f' }}>Role</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: '#00478f' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: '#00478f' }} align="center">
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredUsers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    No users found
                  </TableCell>
                </TableRow>
              ) : (
                filteredUsers.map((user) => (
                  <TableRow key={user.id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar sx={{ width: 36, height: 36, bgcolor: '#0066cc' }}>
                          {`${user.firstName[0]}${user.lastName[0]}`}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                            {`${user.firstName} ${user.lastName}`}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>{user.username}</TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>{user.phone || 'N/A'}</TableCell>
                    <TableCell>
                      {(() => {
                        const r = user.role || '';
                        const upper = r.toUpperCase();
                        if (upper === 'NURSE' || upper === 'CAREGIVER') return 'Caregiver';
                        if (upper === 'LEAD_CAREGIVER') return 'Lead Caregiver';
                        if (upper === 'FACILITY_ADMIN' || upper === 'FACILITY_MANAGER') return 'Facility Manager';
                        return r.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
                      })()}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Chip
                          label={user.isActive !== false ? 'Active' : 'Inactive'}
                          color={user.isActive !== false ? 'success' : 'error'}
                          size="small"
                        />
                        {(user.accountLocked || (user as any).isAccountLocked || securityState[user.username]?.locked) && (
                          <Chip
                            label="Locked"
                            color="error"
                            size="small"
                            icon={<LockIcon sx={{ fontSize: '14px' }} />}
                          />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      {(user.accountLocked || (user as any).isAccountLocked || securityState[user.username]?.locked) && (
                        <Tooltip title="Unlock Account">
                          <IconButton
                            size="small"
                            onClick={() => setUnlockTargetUser(user)}
                            sx={{ color: 'warning.main' }}
                          >
                            <LockOpenIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Reset Password">
                        <IconButton
                          size="small"
                          onClick={() => user.id && handleOpenResetDialog(user.id)}
                          sx={{ color: '#0066cc' }}
                          disabled={!user.id}
                        >
                          <VpnKeyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDialog(user)}
                          sx={{ color: '#0066cc' }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          onClick={() => user.id && handleDeleteUser(user.id)}
                          sx={{ color: 'error.main' }}
                          disabled={!user.id}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Add/Edit User Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ backgroundColor: '#48CAE4', fontWeight: 'bold', color: '#000000' }}>
          {editingUser ? 'Edit User' : 'Add New User'}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Formik
            initialValues={{
              clientId: editingUser?.clientId || '',
              username: editingUser?.username || '',
              fname: editingUser?.firstName || '',
              lname: editingUser?.lastName || '',
              contactNumber: (editingUser?.phone === "N/A" || editingUser?.phone === "NA") ? "" : (editingUser?.phone || ''),
              emailId: editingUser?.email || '',
              bio: editingUser?.notes || '',
              photo: editingUser?.profilePictureUrl || '',
              facilityId: editingUser?.facilityId || '',
              isActive: editingUser?.isActive ?? true,
            }}
            validationSchema={userValidationSchema}
            onSubmit={handleSaveUser}
          >
            {({ errors, touched, values, setFieldValue }) => (
              <Form>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                  <Field
                    as={TextField}
                    name="clientId"
                    label="Client ID"
                    fullWidth
                    size="small"
                    error={touched.clientId && !!errors.clientId}
                    helperText={touched.clientId && errors.clientId}
                  />
                  <Field
                    as={TextField}
                    name="facilityId"
                    label="Facility ID"
                    fullWidth
                    size="small"
                    error={touched.facilityId && !!errors.facilityId}
                    helperText={touched.facilityId && errors.facilityId}
                  />
                  <Field
                    as={TextField}
                    name="username"
                    label="Username"
                    fullWidth
                    size="small"
                    error={touched.username && !!errors.username}
                    helperText={touched.username && errors.username}
                  />
                  <Field
                    as={TextField}
                    name="emailId"
                    label="Email"
                    type="email"
                    fullWidth
                    size="small"
                    error={touched.emailId && !!errors.emailId}
                    helperText={touched.emailId && errors.emailId}
                  />
                  <Field
                    as={TextField}
                    name="fname"
                    label="First Name"
                    fullWidth
                    size="small"
                    error={touched.fname && !!errors.fname}
                    helperText={touched.fname && errors.fname}
                  />
                  <Field
                    as={TextField}
                    name="lname"
                    label="Last Name"
                    fullWidth
                    size="small"
                    error={touched.lname && !!errors.lname}
                    helperText={touched.lname && errors.lname}
                  />
                  <Field
                    as={TextField}
                    name="contactNumber"
                    label="Contact Number"
                    fullWidth
                    size="small"
                    error={touched.contactNumber && !!errors.contactNumber}
                    helperText={touched.contactNumber && errors.contactNumber}
                  />
                  <Field
                    as={TextField}
                    name="bio"
                    label="Bio"
                    fullWidth
                    size="small"
                    error={touched.bio && !!errors.bio}
                    helperText={touched.bio && errors.bio}
                  />
                </Box>

                <DialogActions sx={{ mt: 3 }}>
                  <Button onClick={handleCloseDialog}>Cancel</Button>
                  <Button
                    type="submit"
                    variant="contained"
                    sx={{
                      background: 'linear-gradient(135deg, #0066cc 0%, #00478f 100%)',
                      textTransform: 'none',
                      fontWeight: 'bold',
                    }}
                  >
                    {editingUser ? 'Update' : 'Create'}
                  </Button>
                </DialogActions>
              </Form>
            )}
          </Formik>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={openResetDialog} onClose={handleCloseResetDialog} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ backgroundColor: 'rgba(0, 102, 204, 0.1)', fontWeight: 'bold', color: '#00478f' }}>
          Reset User Password
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Formik
            initialValues={{ newPassword: '', confirmPassword: '' }}
            validationSchema={resetPasswordSchema}
            onSubmit={handleResetPassword}
          >
            {({ errors, touched, values }) => (
              <Form>
                <Box sx={{ display: 'grid', gap: 2 }}>
                  <Field
                    as={TextField}
                    name="newPassword"
                    label="New Password"
                    type={showPassword ? 'text' : 'password'}
                    fullWidth
                    size="small"
                    error={touched.newPassword && !!errors.newPassword}
                    helperText={touched.newPassword && errors.newPassword}
                  />
                  <Field
                    as={TextField}
                    name="confirmPassword"
                    label="Confirm Password"
                    type={showPassword ? 'text' : 'password'}
                    fullWidth
                    size="small"
                    error={touched.confirmPassword && !!errors.confirmPassword}
                    helperText={touched.confirmPassword && errors.confirmPassword}
                  />
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <IconButton
                      size="small"
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                    </IconButton>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      {showPassword ? 'Hide' : 'Show'} password
                    </Typography>
                  </Box>
                  <Typography variant="caption" sx={{ color: 'text.secondary', mt: 1 }}>
                    Password must contain at least 8 characters, including uppercase, lowercase, numbers, and special characters.
                  </Typography>
                </Box>

                <DialogActions sx={{ mt: 3 }}>
                  <Button onClick={handleCloseResetDialog}>Cancel</Button>
                  <Button
                    type="submit"
                    variant="contained"
                    sx={{
                      background: 'linear-gradient(135deg, #0066cc 0%, #00478f 100%)',
                      textTransform: 'none',
                      fontWeight: 'bold',
                    }}
                  >
                    Reset Password
                  </Button>
                </DialogActions>
              </Form>
            )}
          </Formik>
        </DialogContent>
      </Dialog>

      {/* Unlock Confirmation Dialog */}
      <Dialog open={!!unlockTargetUser} onClose={() => { if (!isUnlocking) setUnlockTargetUser(null); }} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ backgroundColor: 'rgba(0, 119, 182, 0.08)', fontWeight: 'bold', color: '#0077B6', display: 'flex', alignItems: 'center', gap: 1 }}>
          <LockOpenIcon /> Unlock Account
        </DialogTitle>
        <DialogContent sx={{ pt: 2, mt: 1 }}>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Are you sure you want to unlock the account for <strong>{unlockTargetUser?.firstName} {unlockTargetUser?.lastName}</strong> ({unlockTargetUser?.username || unlockTargetUser?.email})?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button disabled={isUnlocking} onClick={() => setUnlockTargetUser(null)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={isUnlocking}
            onClick={handleConfirmUnlock}
            sx={{
              backgroundColor: '#0077B6',
              '&:hover': { backgroundColor: '#005F8A' },
              textTransform: 'none',
              fontWeight: 'bold',
            }}
          >
            {isUnlocking ? <CircularProgress size={20} color="inherit" /> : 'Yes, Unlock'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserManagementPage;